import React from 'react'
import PropTypes from 'prop-types'
import DisImage from '../../dis-image'

/**
 * Renders an image with optional title.
 * Intended for use within the Leap components library.
 * Automatically converts Salesforce static URLs to DIS URLs for optimized image delivery.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.src - The image source URL.
 * @param {string} [props.disBaseLink] - DIS base link for image. If not provided, automatically generated from src.
 * @param {string} [props.title] - Optional title attribute for the image.
 * @param {string} [props.alt] - Alt text for accessibility.
 * @param {boolean} [props.preload] - Ignored
 * @param {array|object} [props.widths] - Responsive widths for responsive images. Only used if disBaseLink is provided or auto-generated.
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy for the image - only use 'eager' for above-the-fold images.
 * @param {'high'|'low'|'auto'} [props.fetchpriority] - Browser hint how to prioritize this image download compared to the others.
 * @returns {JSX.Element} An image element.
 */

const Image = ({
    src,
    disBaseLink,
    title,
    alt,
    preload,
    widths,
    loading = 'lazy',
    fetchpriority,
    ...rest
}) => {
    const imageProps = {
        width: '100%',
        height: '100%',
        ...rest,
        alt,
        title,
        loading,
        ...(fetchpriority && {fetchpriority})
    }

    return <DisImage {...imageProps} src={disBaseLink || src} widths={widths} />
}

Image.propTypes = {
    alt: PropTypes.string,
    disBaseLink: PropTypes.string,
    fetchpriority: PropTypes.oneOf(['high', 'low', 'auto']),
    loading: PropTypes.oneOf(['eager', 'lazy']),
    preload: PropTypes.bool,
    src: PropTypes.string.isRequired,
    title: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    draggable: PropTypes.bool
}

export default Image
